// libraries
import type { ProgramIncentives, StateTree } from '@makemydeal/dr-dash-types';
import type { IResponsePayment, IVehicle } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors, selectedOfferSelectors } from '@makemydeal/dr-shared-store';

// types
import type {
    FirstPencilIncentiveOverride,
    FirstPencilIncentiveSelection,
    FirstPencilManualIncentive,
    FirstPencilProgramsOfferType,
    FirstPencilProgramsState,
    FirstPencilSavedProgramsFilters,
    FirstPencilSelectedProgram,
    FirstPencilSetupMode,
    FirstPencilState
} from '../types/firstPencilTypes';

// consts/enums
import { FIRST_PENCIL_SOURCE_APP_NAMES, SALES_CHECK_IN_SOURCE_APPLICATION_NAME } from '../constants';

// selectors
import * as dealModeSelectors from './dealModeSelectors';
import * as dealTemplateSelectors from './dealTemplateSelectors';
import * as multiScenarioSelectors from './multiScenarioSelectors';
import * as offerReduxSelectors from './offerRedux';
import { getApplicationName } from './offerSelectors';

export const getLaunchDealXgId = (state: StateTree): string => {
    const fromLaunch = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const fromOffer = offerReduxSelectors.getDealXgId(state);
    return (fromLaunch || fromOffer || '').trim();
};

export const hasLaunchDealXgId = (state: StateTree): boolean => Boolean(getLaunchDealXgId(state));

// Entry 1 (VinCRM), Entry 2 (Deal List "Create New Deal") and, behind its own toggle, Sales Check-In.
export const isFirstPencilSourcedApplication = (state: StateTree): boolean => {
    const applicationName = getApplicationName(state).trim().toLowerCase();
    if (applicationName === SALES_CHECK_IN_SOURCE_APPLICATION_NAME) {
        return featureToggleSelectors.isFirstPencilSciRequestEnabled(state);
    }
    return FIRST_PENCIL_SOURCE_APP_NAMES.includes(applicationName);
};

export const hasAutoSelectProgramSaved = (state: StateTree): boolean =>
    Boolean(offerReduxSelectors.getCalculationEndpointsState(state)?.autoSelectProgramSavedAt);

// A version-matched saved comparison means the deal has been worked.
export const hasActiveComparison = (state: StateTree): boolean =>
    multiScenarioSelectors.getActiveComparison(state)?.status === 'active';

/** Shared by isFirstPencilLaunch and getAutoApplyBypassReason, so the bypass telemetry fires only for a deal that would otherwise have qualified. */
export const isFirstPencilCandidate = (state: StateTree): boolean =>
    featureToggleSelectors.isFirstPencilWorkflowEnabled(state) &&
    hasLaunchDealXgId(state) &&
    isFirstPencilSourcedApplication(state) &&
    !dealModeSelectors.isFnIDealMode(state);

export const isFirstPencilLaunch = (state: StateTree): boolean => {
    if (!isFirstPencilCandidate(state)) {
        return false;
    }

    if (dealTemplateSelectors.getIsDealTemplateApplied(state) || hasAutoSelectProgramSaved(state)) {
        return false;
    }

    if (hasActiveComparison(state)) {
        return false;
    }

    return true;
};

/** F280192 telemetry: distinguishes "modal correctly suppressed because a template/program already auto-applied" from "not a CRM-launched deal at all" (not a bypass, just not-applicable). */
export const getAutoApplyBypassReason = (state: StateTree): 'templateApplied' | 'autoSelectProgramSaved' | undefined => {
    if (!isFirstPencilCandidate(state)) {
        return undefined;
    }
    if (dealTemplateSelectors.getIsDealTemplateApplied(state)) {
        return 'templateApplied';
    }
    if (hasAutoSelectProgramSaved(state)) {
        return 'autoSelectProgramSaved';
    }
    return undefined;
};

export const getFirstPencilState = (state: StateTree): FirstPencilState | undefined => state.firstPencil;

export const getShowCreateDealModal = (state: StateTree): boolean => Boolean(getFirstPencilState(state)?.pendingCreate);

export const isApplyingSelection = (state: StateTree): boolean => Boolean(getFirstPencilState(state)?.applying);

export const hasApplyError = (state: StateTree): boolean => Boolean(getFirstPencilState(state)?.applyError);

export const isTemplateApplyOverlayShown = (state: StateTree): boolean =>
    getShowCreateDealModal(state) && isApplyingSelection(state) && getSelectedSetupMode(state) === 'template';

// Released once Next is pressed: the apply's own payment work must not be held behind the open slide-out.
export const isPaymentSuppressedPendingCreate = (state: StateTree): boolean =>
    getShowCreateDealModal(state) && !isApplyingSelection(state);

/**
 * No worked scenario exists to clone yet, so "New Scenario" stays hidden while the intercept is pending.
 * Consumed as one term of `canCreateScenario` in dash's additionalSelectors.ts (mvCanCreateScenario), which
 * also carries the Manager-View-only host gate — this selector alone only speaks to the pending-intercept part.
 */
export const allowsScenarioCreation = (state: StateTree): boolean => !getFirstPencilState(state)?.pendingCreate;

export const getSelectedSetupMode = (state: StateTree): FirstPencilSetupMode | undefined => getFirstPencilState(state)?.setupMode;

export const getSelectedTemplateId = (state: StateTree): string | undefined => getFirstPencilState(state)?.selectedTemplateId;

export const getSelectedTemplateName = (state: StateTree): string | undefined => getFirstPencilState(state)?.selectedTemplateName;

export const getSelectedProgram = (state: StateTree): FirstPencilSelectedProgram | undefined =>
    getFirstPencilState(state)?.selectedProgram;

export const getActiveProgramsOfferType = (state: StateTree): FirstPencilProgramsOfferType | undefined =>
    getFirstPencilState(state)?.activeProgramsOfferType;

export const getProgramsFilters = (
    state: StateTree,
    offerType: FirstPencilProgramsOfferType
): FirstPencilSavedProgramsFilters | undefined => getFirstPencilState(state)?.programsFilters?.[offerType];

export const getSelectedVehicle = (state: StateTree): IVehicle | undefined => getFirstPencilState(state)?.selectedVehicle;

export const getSelectedIncentives = (state: StateTree): FirstPencilIncentiveSelection | undefined =>
    getFirstPencilState(state)?.selectedIncentives;

const EMPTY_MANUAL_INCENTIVES: FirstPencilManualIncentive[] = [];

export const getManualIncentives = (state: StateTree): FirstPencilManualIncentive[] =>
    getFirstPencilState(state)?.manualIncentives ?? EMPTY_MANUAL_INCENTIVES;

export const getIncentiveOverrides = (state: StateTree): Record<number, FirstPencilIncentiveOverride> | undefined =>
    getFirstPencilState(state)?.incentiveOverrides;

export const getSelectedIncentiveOverrides = (state: StateTree): FirstPencilIncentiveOverride[] => {
    const selection = getSelectedIncentives(state);
    if (!selection) {
        return [];
    }
    const selectedIds = [...selection.conditionalIds, ...selection.nonConditionalIds];
    return Object.values(getIncentiveOverrides(state) ?? {}).filter((override) => selectedIds.includes(override.incentiveId));
};

export const hasInvalidIncentiveOverrides = (state: StateTree): boolean =>
    getSelectedIncentiveOverrides(state).some((override) => !override.name.trim() || override.amount <= 0);

export const hasDuplicateManualIncentiveCodes = (state: StateTree): boolean => {
    const codes = getManualIncentives(state)
        .filter((incentive) => !incentive.deleted && incentive.code != null)
        .map((incentive) => incentive.code);
    return new Set(codes).size !== codes.length;
};

export const isFirstPencilFlowActive = (state: StateTree): boolean =>
    featureToggleSelectors.isFirstPencilWorkflowEnabled(state) && Boolean(getFirstPencilState(state)?.pendingCreate);

const EMPTY_QUOTES: readonly IResponsePayment[] = [];

export const getProgramsState = (state: StateTree): FirstPencilProgramsState | undefined => getFirstPencilState(state)?.programs;

export const getProgramsOfferType = (state: StateTree): FirstPencilProgramsOfferType | undefined =>
    getProgramsState(state)?.offerType;

export const getProgramQuotes = (state: StateTree): readonly IResponsePayment[] => getProgramsState(state)?.quotes ?? EMPTY_QUOTES;

export const getProgramsIncentives = (state: StateTree): Partial<ProgramIncentives> | undefined =>
    getProgramsState(state)?.incentives;

export const isProgramsLoading = (state: StateTree): boolean => {
    const programs = getProgramsState(state);
    return !programs || programs.pendingRequests > 0;
};

export const hasProgramsError = (state: StateTree): boolean => {
    const programs = getProgramsState(state);
    if (!programs?.error) {
        return false;
    }
    return !programs.pendingRequests && !programs.quotes.length;
};

// A path alone is not enough; the chosen path must carry a selection, or Next is a dead end.
export const canConfirmCreate = (state: StateTree): boolean => {
    switch (getSelectedSetupMode(state)) {
        case 'template':
            return Boolean(getSelectedTemplateId(state));
        case 'programs':
            return (
                Boolean(getSelectedProgram(state)) &&
                !hasDuplicateManualIncentiveCodes(state) &&
                !hasInvalidIncentiveOverrides(state)
            );
        default:
            return false;
    }
};
